import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const ProcessVisualization = ({ type, data, isActive }) => {
  const [animationStep, setAnimationStep] = useState(0);

  useEffect(() => {
    if (isActive) {
      const interval = setInterval(() => {
        setAnimationStep(prev => (prev + 1) % 4);
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [isActive]);

  const renderCodeVisualization = () => (
    <div className="space-y-4">
      <div className="flex items-center space-x-2 mb-4">
        <Icon name="Code" size={20} className="text-accent" />
        <h4 className="font-headline font-bold text-lg text-foreground">
          Development Process
        </h4>
      </div>
      
      <div className="bg-surface/30 rounded-lg p-4 font-accent text-sm">
        <div className="space-y-2">
          {data?.codeSteps?.map((step, index) => (
            <div
              key={index}
              className={`transition-all duration-500 ${
                isActive && index <= animationStep
                  ? 'text-accent opacity-100' :'text-muted-foreground opacity-50'
              }`}
            >
              <span className="text-muted-foreground mr-2">{index + 1}.</span>
              {step}
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="bg-surface/20 rounded-lg p-3">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="GitBranch" size={16} className="text-accent" />
            <span className="font-body font-medium text-sm text-foreground">
              Version Control
            </span>
          </div>
          <div className="text-xs text-muted-foreground">
            {data?.commits} commits • {data?.branches} branches
          </div>
        </div>
        
        <div className="bg-surface/20 rounded-lg p-3">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="TestTube" size={16} className="text-accent" />
            <span className="font-body font-medium text-sm text-foreground">
              Testing
            </span>
          </div>
          <div className="text-xs text-muted-foreground">
            {data?.testCoverage}% coverage • {data?.tests} tests
          </div>
        </div>
      </div>
    </div>
  );

  const renderDesignVisualization = () => (
    <div className="space-y-4">
      <div className="flex items-center space-x-2 mb-4">
        <Icon name="Palette" size={20} className="text-accent" />
        <h4 className="font-headline font-bold text-lg text-foreground">
          Design Evolution
        </h4>
      </div>

      <div className="grid grid-cols-3 gap-4">
        {data?.designStages?.map((stage, index) => (
          <div
            key={index}
            className={`relative bg-surface/30 rounded-lg p-4 transition-all duration-500 ${
              isActive && index <= animationStep
                ? 'border border-accent cosmic-glow' :'border border-border/30'
            }`}
          >
            <div className="aspect-square bg-gradient-to-br from-accent/20 to-cosmic-energy/20 rounded-lg mb-3 flex items-center justify-center">
              <Icon name={stage?.icon} size={24} className="text-accent" />
            </div>
            <h5 className="font-body font-medium text-sm text-foreground mb-1">
              {stage?.name}
            </h5>
            <p className="font-body text-xs text-muted-foreground">
              {stage?.description}
            </p>
            
            {isActive && index <= animationStep && (
              <div className="absolute -top-1 -right-1 w-4 h-4 bg-accent rounded-full flex items-center justify-center">
                <Icon name="Check" size={10} className="text-background" />
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="bg-surface/20 rounded-lg p-4">
        <h5 className="font-body font-semibold text-sm text-foreground mb-3">
          Design Iterations
        </h5>
        <div className="flex items-center space-x-4">
          <div className="flex-1 bg-border/30 rounded-full h-2 overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-accent to-cosmic-energy transition-all duration-1000"
              style={{ width: isActive ? `${((animationStep + 1) / 4) * 100}%` : '0%' }}
            ></div>
          </div>
          <span className="font-body text-xs text-muted-foreground">
            {data?.iterations} iterations
          </span>
        </div>
      </div>
    </div>
  );

  const renderResearchVisualization = () => (
    <div className="space-y-4">
      <div className="flex items-center space-x-2 mb-4">
        <Icon name="Search" size={20} className="text-accent" />
        <h4 className="font-headline font-bold text-lg text-foreground">
          Research & Analysis
        </h4>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-3">
          <h5 className="font-body font-semibold text-sm text-foreground">
            User Research
          </h5>
          {data?.researchMethods?.map((method, index) => (
            <div
              key={index}
              className={`flex items-center space-x-3 p-3 rounded-lg transition-all duration-500 ${
                isActive && index <= animationStep
                  ? 'bg-accent/10 border border-accent/30' :'bg-surface/30 border border-border/30'
              }`}
            >
              <Icon name={method?.icon} size={16} className="text-accent" />
              <div>
                <span className="font-body font-medium text-sm text-foreground">
                  {method?.name}
                </span>
                <div className="font-body text-xs text-muted-foreground">
                  {method?.participants} participants
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="space-y-3">
          <h5 className="font-body font-semibold text-sm text-foreground">
            Key Insights
          </h5>
          {data?.insights?.map((insight, index) => (
            <div
              key={index}
              className={`p-3 rounded-lg transition-all duration-500 ${
                isActive && index <= animationStep
                  ? 'bg-success/10 border border-success/30' :'bg-surface/30 border border-border/30'
              }`}
            >
              <p className="font-body text-sm text-foreground">
                {insight}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="bg-card rounded-xl border border-border p-6">
      {type === 'code' && renderCodeVisualization()}
      {type === 'design' && renderDesignVisualization()}
      {type === 'research' && renderResearchVisualization()}
    </div>
  );
};

export default ProcessVisualization;